import { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { CloseCircleOutlined } from '@ant-design/icons'

import { colours } from '@qflow/theme'
import { zeroPad, yearPad } from 'admin/lib/dates'

export function DateTimeUS(props) {
  const [focusedElementIndex, setFocusedElementIndex] = useState()
  const [month, setMonth] = useState()
  const [day, setDay] = useState()
  const [year, setYear] = useState()
  const [hours, setHours] = useState()
  const [minutes, setMinutes] = useState()
  const [amPm, setAmPm] = useState('AM')

  const monthRef = useRef()
  const dayRef = useRef()
  const yearRef = useRef()
  const hoursRef = useRef()
  const minutesRef = useRef()
  const amPmRef = useRef()

  const focusableElementRefArray = useMemo(
    () => [monthRef, dayRef, yearRef, hoursRef, minutesRef, amPmRef],
    []
  )

  const MIN_YEAR = 2000
  const MAX_YEAR = 2050
  const DATE_TIME_SPLIT_REGEX = useMemo(() => /[/:, ]+/, [])

  useEffect(() => {
    const valueArray = props.value?.split(DATE_TIME_SPLIT_REGEX)
    if (valueArray?.length === 6) {
      setMonth(zeroPad(valueArray[0]))
      setDay(zeroPad(valueArray[1]))
      setYear(valueArray[2])
      setHours(valueArray[3])
      setMinutes(zeroPad(valueArray[4]))
      setAmPm(valueArray[5])
    } else {
      setAmPm('AM')
    }
  }, [DATE_TIME_SPLIT_REGEX, props, props.value])

  useEffect(() => {
    if (
      focusedElementIndex !== null &&
      focusedElementIndex !== undefined &&
      focusableElementRefArray &&
      focusableElementRefArray[focusedElementIndex]
    ) {
      focusableElementRefArray[focusedElementIndex].current.focus()
      focusableElementRefArray[focusedElementIndex].current.select()
    }
  }, [focusableElementRefArray, focusedElementIndex])

  const handleClick = elementIndex => {
    setFocusedElementIndex(elementIndex)
  }

  const handleFocus = elementIndex => {
    setFocusedElementIndex(elementIndex)

    // Handle Click from OCR item returning focus
    const clipboardValue = sessionStorage.getItem('clipboard')
    sessionStorage.removeItem('clipboard')

    if (clipboardValue && clipboardValue !== '') {
      // split clipboardValue in to parts
      const valueArray = clipboardValue.split(DATE_TIME_SPLIT_REGEX)
      const currentValuesObj = {
        month,
        day,
        year,
        hours,
        minutes,
        amPm
      }
      // If 3 parts to the array is probably date
      if (valueArray.length === 3) {
        const newMonth = zeroPad(valueArray[0])
        setMonth(newMonth)
        currentValuesObj.month = newMonth
        const newDay = zeroPad(valueArray[1])
        setDay(newDay)
        currentValuesObj.day = newDay
        const newYear = yearPad(valueArray[2])
        setYear(newYear)
        currentValuesObj.year = newYear
      }
      // If 2 parts to the array is probably time
      if (valueArray.length === 2) {
        const newHours = valueArray[0]
        setHours(newHours)
        currentValuesObj.hours = newHours
        const newMinutes = zeroPad(valueArray[1])
        setMinutes(newMinutes)
        currentValuesObj.minutes = newMinutes
      }
      const usDateString = `${currentValuesObj.month}/${currentValuesObj.day}/${currentValuesObj.year}, ${currentValuesObj.hours}:${currentValuesObj.minutes} ${currentValuesObj.amPm}`
      props.handleDateBlur(usDateString)
    }
  }

  const handleKeyPress = event => {
    if (event.keyCode === 39) {
      // Right arrow
      event.preventDefault()
      const newIndex =
        focusedElementIndex + 1 >= focusableElementRefArray.length
          ? focusedElementIndex
          : focusedElementIndex + 1
      setFocusedElementIndex(newIndex)
    } else if (event.keyCode === 37) {
      // Left arrow
      event.preventDefault()
      const newIndex =
        focusedElementIndex - 1 < 0
          ? focusedElementIndex
          : focusedElementIndex - 1
      setFocusedElementIndex(newIndex)
    }

    // AM/PM functionality
    else if (focusedElementIndex === 5) {
      if (event.keyCode === 38 || event.keyCode === 40) {
        // Up and Down Arrow key toggles between AM and PM
        toggleAmPm()
        event.preventDefault()
      } else if (event.key === 'a') {
        setAmPm('AM')
      } else if (event.key === 'p') {
        setAmPm('PM')
      }
    }
  }

  const handleValueChange = event => {
    const name = event.target.name
    const value = event.target.value

    switch (name) {
      case 'month':
        setMonth(value)
        if (value.length >= 2) {
          setFocusedElementIndex(prev => prev + 1)
        }
        break
      case 'day':
        setDay(value)
        if (value.length >= 2) {
          setFocusedElementIndex(prev => prev + 1)
        }
        break
      case 'year':
        setYear(value)
        if (value.length >= 4) {
          setFocusedElementIndex(prev => prev + 1)
        }
        break
      case 'hours':
        setHours(value)
        if (value.length >= 2) {
          setFocusedElementIndex(prev => prev + 1)
        }
        break
      case 'minutes':
        setMinutes(value)
        if (value.length >= 2) {
          setFocusedElementIndex(prev => prev + 1)
        }
        break
    }
  }

  const handleBlur = event => {
    // First create an object of all current values so the one changed value can be easily overwritten
    const currentValuesObj = {
      month,
      day,
      year,
      hours,
      minutes,
      amPm
    }

    const name = event.target.name
    let value = event.target.value

    // Reformat value where required
    if (name !== 'hours' && name !== 'year' && value) {
      value = zeroPad(value)
    }
    if (name !== 'year' && value && value.length > 2) {
      // remove starting character
      value = value.slice(1)
    }

    switch (name) {
      case 'month': {
        const checkedValue = value > 12 ? 12 : value < 1 ? '01' : value
        setMonth(checkedValue)
        currentValuesObj.month = checkedValue
        break
      }
      case 'day': {
        const checkedValue = value > 31 ? 31 : value < 1 ? '01' : value
        setDay(checkedValue)
        currentValuesObj.day = checkedValue
        break
      }
      case 'year': {
        const checkedValue =
          value > MAX_YEAR ? MAX_YEAR : value < MIN_YEAR ? MIN_YEAR : value
        setYear(checkedValue)
        currentValuesObj.year = checkedValue
        break
      }
      case 'hours': {
        const checkedValue = value > 12 ? 12 : value < 1 ? 1 : value
        setHours(checkedValue)
        currentValuesObj.hours = checkedValue
        break
      }
      case 'minutes': {
        const checkedValue = value > 59 ? 59 : value <= 0 ? '00' : value
        setMinutes(checkedValue)
        currentValuesObj.minutes = checkedValue
        break
      }
    }

    // Create single string and pass to parent component
    if (
      currentValuesObj.month &&
      currentValuesObj.day &&
      currentValuesObj.year &&
      currentValuesObj.hours &&
      currentValuesObj.minutes &&
      currentValuesObj.amPm
    ) {
      const usDateString = `${currentValuesObj.month}/${currentValuesObj.day}/${currentValuesObj.year}, ${currentValuesObj.hours}:${currentValuesObj.minutes} ${currentValuesObj.amPm}`
      props.handleDateBlur(usDateString)
    }
  }

  const toggleAmPm = () => {
    if (amPm === 'AM') {
      setAmPm('PM')
    } else {
      setAmPm('AM')
    }
  }

  const clearAll = () => {
    setMonth('')
    setDay('')
    setYear('')
    setHours('')
    setMinutes('')
    setAmPm('AM')

    props.handleDateBlur('')
  }

  return (
    <DateTimeUSWrapper
      className="form-input DateTimeUS"
      disabled={props.disabled}
    >
      <InputsContainer disabled={props.disabled}>
        <input
          name="month"
          id={`${props.id}-month`}
          relatedfieldpath={props.relatedFieldPath}
          ref={monthRef}
          value={month}
          type="number"
          placeholder="MM"
          min="1"
          max="12"
          onChange={handleValueChange}
          onClick={() => handleClick(0)}
          onFocus={() => handleFocus(0)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
        /
        <input
          name="day"
          id={`${props.id}-day`}
          relatedfieldpath={props.relatedFieldPath}
          ref={dayRef}
          value={day}
          type="number"
          placeholder="DD"
          min="1"
          max="31"
          tabIndex="-1"
          onChange={handleValueChange}
          onClick={() => handleClick(1)}
          onFocus={() => handleFocus(1)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
        /
        <input
          name="year"
          id={`${props.id}-year`}
          relatedfieldpath={props.relatedFieldPath}
          ref={yearRef}
          value={year}
          type="number"
          placeholder="YYYY"
          min={MIN_YEAR}
          max={MAX_YEAR}
          style={{ width: '35px' }}
          tabIndex="-1"
          onChange={handleValueChange}
          onClick={() => handleClick(2)}
          onFocus={() => handleFocus(2)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
        ,
        <input
          name="hours"
          id={`${props.id}-hours`}
          relatedfieldpath={props.relatedFieldPath}
          ref={hoursRef}
          value={hours}
          type="number"
          placeholder="hh"
          min="1"
          max="12"
          style={{ textAlign: 'right', paddingRight: '2px' }}
          tabIndex="-1"
          onChange={handleValueChange}
          onClick={() => handleClick(3)}
          onFocus={() => handleFocus(3)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
        :
        <input
          name="minutes"
          id={`${props.id}-minutes`}
          relatedfieldpath={props.relatedFieldPath}
          ref={minutesRef}
          value={minutes}
          type="number"
          placeholder="mm"
          min="0"
          max="59"
          style={{ textAlign: 'left', paddingLeft: '2px' }}
          tabIndex="-1"
          onChange={handleValueChange}
          onClick={() => handleClick(4)}
          onFocus={() => handleFocus(4)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
        <input
          name="am-pm"
          id={`${props.id}-am-pm`}
          relatedfieldpath={props.relatedFieldPath}
          ref={amPmRef}
          value={amPm}
          readOnly
          tabIndex="-1"
          onClick={() => handleClick(5)}
          onFocus={() => handleFocus(5)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
          disabled={props.disabled}
        />
      </InputsContainer>
      {!props.disabled && <CloseCircleOutlined onClick={clearAll} />}
    </DateTimeUSWrapper>
  )
}

export const DateTimeUSWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${colours.CLOUD_GREY};
  border-radius: 5px;
  padding: 4px 11px;
  justify-content: space-between;

  ${props =>
    !props.disabled &&
    css`
      &:hover,
      &:focus-within {
        border-color: ${colours.TEAL};
      }
    `}

  ${props =>
    props.disabled &&
    css`
      background-color: ${colours.CONTROL_PANEL_GREY};
    `}
`

export const InputsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;

  input {
    border: none;
    padding: 0;
    text-align: center;
    width: 25px;
  }

  input:focus-visible {
    outline: none;
    background-color: ${colours.TEAL};
  }

  // Hide arrows
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`
